import { SearchMode, SearchType } from '@aibees/elephant/types';
import { HttpMethod, Maybe } from '@aibees/shared/definition';
import { useBaseQuery } from '@aibees/shared/http';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { isEmpty, omit } from 'lodash-es';
import { useDeferredValue } from 'react';
import {
  COMPANY_SEARCH_API_METADATA_PREFIX,
  PEOPLE_SEARCH_API_METADATA_PREFIX,
} from '../constants';
import { useSearchFiltersStore } from '../feature/search-filters/store';
import { CompanySearchBody } from '../feature/search-table-company/hooks/types';
import { transform as companyRequestTransform } from '../feature/search-table-company/hooks/use-browse-company-search';
import { transform as peopleRequestTransform } from '../feature/search-table-people/hooks/use-browse-people-search';
import {
  BrowseMetadataRequest,
  BrowseMetadataResponse,
  MetadataType,
} from './types';
import {
  useSearchModeFromRoute,
  useSearchTypeFromRoute,
} from './use-obtain-search-type-from-route';
import { useSearchQueryFilter } from './use-search-query-filter';

export function useBrowseMetadata(
  request: BrowseMetadataRequest,
  queryParams?: Record<string, any>,
  enabled?: boolean
) {
  const workspace = useWorkspace()?.id;
  const { params } = useSearchQueryFilter();
  const deferredParams = useDeferredValue(params);
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();

  const current = useSearchFiltersStore.useCurrent(); // TODO: we need an enum for filter's names for avoiding bugs here
  let url: string;

  if (type === SearchType.People) {
    url = PEOPLE_SEARCH_API_METADATA_PREFIX;
  } else {
    url = COMPANY_SEARCH_API_METADATA_PREFIX;
  }

  const reqParams = objSanitizeNilValues(
    omit({ ...request, ...deferredParams }, 'type', 'page', 'size')
  );

  const transformedRequestParams = {
    ...transform(reqParams, type, mode, request.metadataType, current),
    ...{
      metadataType: request.metadataType,
    },
  };

  return useBaseQuery<
    CompanySearchBody & BrowseMetadataRequest,
    BrowseMetadataResponse
  >(
    [
      useBrowseMetadata.name,
      type,
      queryParams,
      {
        ...transformedRequestParams,
      },
    ],
    {
      url: url?.replace(':wid', String(workspace)),
      method: HttpMethod.post,
      params: queryParams,
      data: transformedRequestParams,
    },
    { enabled }
  );
}

// transform the search request body to the metadata request body
export function transform(
  r: Record<string, any>,
  type: Maybe<SearchType>,
  mode: Maybe<SearchMode>,
  metadataType: MetadataType,
  currentFilter: string | false
) {
  const req =
    type === SearchType.People
      ? peopleRequestTransform(r)
      : companyRequestTransform(r, mode);

  if (
    metadataType === MetadataType.COMPANY_EMPLOYEE_SIZE &&
    currentFilter === 'Employee Size'
  ) {
    req.account = omit(req.account, 'employeeSize');
  }

  if (
    metadataType === MetadataType.COMPANY_FUNDING_TYPE &&
    currentFilter === 'Funding'
  ) {
    req.account.funding = omit(req.account.funding, 'type');
    if (isEmpty(req.account.funding)) {
      req.account = omit(req.account, 'funding');
    }
  }

  if (
    metadataType === MetadataType.COMPANY_REVENUE &&
    currentFilter === 'Annual Revenue'
  ) {
    req.account = omit(req.account, 'revenue');
  }

  if (
    metadataType === MetadataType.COMPANY_SOCIAL_MEDIA &&
    currentFilter === 'Social Media'
  ) {
    req.account = omit(req.account, 'socialMedia');
  }

  if (
    metadataType === MetadataType.PERSON_SOCIAL_MEDIA &&
    currentFilter === 'Social Media'
  ) {
    req.contact = omit(req.contact, 'socialMedia');
  }

  if (
    metadataType === MetadataType.COMPANY_LINKEDIN_FOLLOWERS &&
    currentFilter === 'Social Media Followers' &&
    req.account.socialMediaFollower?.linkedin
  ) {
    req.account.socialMediaFollower.linkedin = omit(
      req.account.socialMediaFollower.linkedin,
      'followers'
    );
    if (isEmpty(req.account.socialMediaFollower.linkedin)) {
      req.account.socialMediaFollower = omit(
        req.account.socialMediaFollower,
        'linkedin'
      );
    }
    if (isEmpty(req.account.socialMediaFollower)) {
      req.account = omit(req.account, 'socialMediaFollower');
    }
  }

  if (
    metadataType === MetadataType.PERSON_LINKEDIN_FOLLOWERS &&
    currentFilter === 'Social Media Followers' &&
    req.contact.socialMediaFollower?.linkedin
  ) {
    req.contact.socialMediaFollower.linkedin = omit(
      req.contact.socialMediaFollower.linkedin,
      'followers'
    );
    if (isEmpty(req.contact.socialMediaFollower.linkedin)) {
      req.contact.socialMediaFollower = omit(
        req.contact.socialMediaFollower,
        'linkedin'
      );
    }
    if (isEmpty(req.contact.socialMediaFollower)) {
      req.contact = omit(req.contact, 'socialMediaFollower');
    }
  }

  if (
    metadataType === MetadataType.PERSON_LINKEDIN_CONNECTIONS &&
    currentFilter === 'Social Media Followers' &&
    req.contact.socialMediaFollower?.linkedin
  ) {
    req.contact.socialMediaFollower.linkedin = omit(
      req.contact.socialMediaFollower.linkedin,
      'connections'
    );
    if (isEmpty(req.contact.socialMediaFollower.linkedin)) {
      req.contact.socialMediaFollower = omit(
        req.contact.socialMediaFollower,
        'linkedin'
      );
    }
    if (isEmpty(req.contact.socialMediaFollower)) {
      req.contact = omit(req.contact, 'socialMediaFollower');
    }
  }

  if (
    metadataType === MetadataType.PERSON_PROFILE_BADGE &&
    currentFilter === 'Profile Badge'
  ) {
    req.contact = omit(req.contact, 'profileBadge');
  }

  if (
    metadataType === MetadataType.COMPANY_TYPE &&
    currentFilter === 'Company Type'
  ) {
    req.account = omit(req.account, 'type');
  }

  if (
    metadataType === MetadataType.COMPANY_LANGUAGE &&
    currentFilter === 'Operation Language'
  ) {
    req.account = omit(req.account, 'language');
  }

  if (
    metadataType === MetadataType.PERSON_LANGUAGE &&
    currentFilter === 'Language Skills'
  ) {
    req.contact = omit(req.contact, 'language');
  }

  if (
    metadataType === MetadataType.PERSON_SENIORITY &&
    currentFilter === 'Job Role'
  ) {
    req.contact = omit(req.contact, 'seniority');
  }

  if (
    metadataType === MetadataType.COMPANY_EMPLOYEE_SENIORITY &&
    currentFilter === 'Job Role' &&
    !!req.account.employee
  ) {
    req.account.employee = omit(req.account.employee, 'seniority');
  }

  if (
    metadataType === MetadataType.COMPANY_DEPARTMENT &&
    currentFilter === 'Employee By Department'
  ) {
    if (req.account.metrics) {
      req.account.metrics = omit(req.account.metrics, 'employee');
    }
  }

  if (
    metadataType === MetadataType.COMPANY_DEPARTMENT &&
    currentFilter === 'Headcount Growth'
  ) {
    if (req.account.metrics) {
      req.account.metrics = omit(req.account.metrics, 'growth');
    }
  }

  return req;
}
